import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { updatePreferences } from '@/lib/api';

/**
 * Driver EVII voice-mode state. Mirrors the admin store's voice work
 * (apps/admin/src/store/evie.ts) — presentation/input state only, no data access.
 *
 *   1. voiceActive — the hands-free loop (listen -> send -> speak) is live.
 *      In-memory only, so a reload starts voice off. The DriverAssistSheet owns
 *      the loop; this flag drives the mic state.
 *   2. voiceByUser — the per-driver EVII voice choice (female | male), persisted
 *      to localStorage and keyed by user id (the same per-user pattern the rest
 *      of the app uses), so each driver's choice sticks for them. Default female
 *      -- the map holds only explicit choices, read with a `?? 'female'`.
 */

export type EvieVoiceChoice = 'female' | 'male';

interface EvieVoiceState {
  voiceActive: boolean;
  setVoiceActive: (active: boolean) => void;
  toggleVoice: () => void;

  voiceByUser: Record<string, EvieVoiceChoice>;
  /** Set the driver's voice: updates the local cache immediately AND persists
   *  to the server (fire-and-forget) so the choice follows them across
   *  devices. Every call site (Settings, the tour voice-pick step) gets the
   *  server write for free. */
  setVoiceChoice: (userId: string | null | undefined, choice: EvieVoiceChoice) => void;
  /** Seed the local cache from the SERVER value (no server write). Called
   *  once on app load by useEvieVoiceSync so a fresh device picks up the
   *  driver's server-side choice. */
  hydrateVoice: (userId: string | null | undefined, choice: EvieVoiceChoice) => void;

  /** Whether the mic auto-starts when the EVII chat opens. Per-user, persisted.
   *  Default ON (read with `?? true`); off makes the mic wait for a tap. */
  micAutostartByUser: Record<string, boolean>;
  setMicAutostart: (userId: string | null | undefined, on: boolean) => void;
}

export const useEvieVoiceStore = create<EvieVoiceState>()(
  persist(
    (set) => ({
      voiceActive: false,
      setVoiceActive: (active) => set({ voiceActive: active }),
      toggleVoice: () => set((s) => ({ voiceActive: !s.voiceActive })),

      voiceByUser: {},
      setVoiceChoice: (userId, choice) =>
        set((s) => {
          if (!userId) return {};
          // Persist to the server so the choice follows the driver across
          // devices; fire-and-forget — the local cache is the source of
          // truth for immediate playback, the server for cross-device.
          void updatePreferences({ evie_voice: choice }).catch(() => {});
          return { voiceByUser: { ...s.voiceByUser, [userId]: choice } };
        }),
      hydrateVoice: (userId, choice) =>
        set((s) => {
          if (!userId) return {};
          // Seed only — never write back to the server (this IS the server
          // value). Overwrites any stale local cache so the server wins.
          return { voiceByUser: { ...s.voiceByUser, [userId]: choice } };
        }),

      micAutostartByUser: {},
      setMicAutostart: (userId, on) =>
        set((s) => {
          if (!userId) return {};
          return { micAutostartByUser: { ...s.micAutostartByUser, [userId]: on } };
        }),
    }),
    {
      name: 'evi-web-evie-voice',
      // Persist the per-user voice choice + mic auto-start pref; voiceActive
      // stays in-memory so voice mode is off on every load.
      partialize: (s) => ({
        voiceByUser: s.voiceByUser,
        micAutostartByUser: s.micAutostartByUser,
      }),
    },
  ),
);
